import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaCheck,
  FaBuilding,
  FaClipboardList,
  FaFileAlt,
  FaUsers,
} from "react-icons/fa";

interface Props {
  currentStep: number;
  onStepChange: (step: number) => void;
  steps: {
    id: number;
    title: string;
  }[];
  mode?: string;
}

const stepIcons = {
  1: FaBuilding,
  2: FaClipboardList,
  3: FaUsers,
  4: FaFileAlt,
};

export default function CompanyStepper({
  currentStep,
  onStepChange,
  steps,
  mode,
}: Props) {
  const { i18n } = useTranslation();
  const isViewMode = mode === "view";
  const isRTL = i18n.language === "fa" || i18n.language === "ar";

  return (
    <div className="relative w-full overflow-visible px-2 py-6 sm:px-4">
      <div className="flex items-start justify-between overflow-visible">
        {steps.map((item, index) => {
          const isActive = currentStep === item.id;
          const isCompleted = currentStep > item.id;
          const Icon = stepIcons[item.id as keyof typeof stepIcons];

          return (
            <div key={item.id} className="relative flex-1 overflow-visible">
              {index < steps.length - 1 && (
                <div
                  className={`absolute top-6 w-full -translate-y-1/2 ${
                    isRTL ? "right-1/2" : "left-1/2"
                  }`}
                >
                  <div
                    className={`h-0.5 w-full transition-all duration-500 ${
                      isCompleted
                        ? "bg-gradient-to-r from-indigo-400 to-indigo-500 dark:from-indigo-500 dark:to-indigo-600"
                        : "bg-gray-200 dark:bg-white/10"
                    }`}
                    style={{ opacity: isCompleted ? 0.6 : 1 }}
                  />
                </div>
              )}
              {/* Step Button */}
              <button
                type="button"
                onClick={() => {
                  if (isViewMode) {
                    onStepChange(item.id);
                  }
                }}
                disabled={!isViewMode}
                className="group relative z-10 flex w-full flex-col items-center gap-3 overflow-visible"
                style={{ cursor: isViewMode ? "pointer" : "default" }}
              >
                <div className="relative overflow-visible">
                  <motion.div
                    initial={false}
                    animate={{
                      scale: isActive ? 1.1 : 1,
                    }}
                    transition={{ duration: 0.2 }}
                    className={`
                      flex h-12 w-12 items-center justify-center rounded-full transition-all duration-300
                      sm:h-14 sm:w-14
                      ${
                        isActive
                          ? "scale-110 bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-lg shadow-indigo-500/30"
                          : isCompleted
                          ? "bg-gradient-to-br from-green-500 to-green-600 text-white"
                          : "bg-gray-200 text-gray-500 dark:bg-white/10 dark:text-gray-400"
                      }
                    `}
                  >
                    {isCompleted ? (
                      <FaCheck className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}

                    {/* Active pulse effect */}
                    {isActive && !isCompleted && (
                      <motion.span
                        initial={{ scale: 1, opacity: 0.6 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "easeOut",
                        }}
                        className="absolute inset-0 rounded-full bg-indigo-400"
                      />
                    )}
                  </motion.div>
                </div>

                {/* Title */}
                <div className="mt-2 text-center">
                  <span
                    className={`text-[10px] font-medium transition-all duration-300 sm:text-sm ${
                      isActive
                        ? "text-indigo-600 dark:text-indigo-400"
                        : isCompleted
                        ? "text-green-600 dark:text-green-400"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {item.title}
                  </span>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
